/**
 * Toast Component
 * 
 * PlayStation-style notification component displaying temporary messages with different types
 * (success, error, info, warning). Features logo-anchored design where toast emerges from
 * a padded logo on the right side, with dark mode support, progress bar, and smooth animations.
 */

import React, { useEffect, useState } from 'react';
import { useTheme } from '@/app/providers/ThemeContext.jsx';
import '/styles/components/toast.css';
import logoDark from '@/assets/images/loading-dark.png';
import logoLight from '@/assets/images/loading.png';

const Toast = ({ message, type = 'success', onClose, duration = 3000 }) => {
  const { effectiveTheme } = useTheme();
  const [progress, setProgress] = useState(100);
  const [isClosing, setIsClosing] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(true);

  const logoSrc = effectiveTheme === 'dark' ? logoDark : logoLight;

  useEffect(() => {
    // Preload logo images
    const preloadImages = () => {
      const lightImg = new Image();
      const darkImg = new Image();
      lightImg.src = logoLight;
      darkImg.src = logoDark;

      const checkImage = new Image();
      checkImage.onload = () => setImageLoaded(true);
      checkImage.onerror = () => setImageLoaded(true);
      checkImage.src = logoSrc;

      if (checkImage.complete || checkImage.naturalWidth > 0) {
        setImageLoaded(true);
      }
    };

    preloadImages();
  }, [logoSrc]);

  useEffect(() => {
    if (duration > 0) {
      const interval = 50; // Update every 50ms for smooth progress
      const decrement = (100 / duration) * interval;

      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          const newProgress = prev - decrement;
          if (newProgress <= 0) {
            setIsClosing(true);
            setTimeout(() => {
              onClose();
            }, 400); // Wait for exit animation
            return 0;
          }
          return newProgress;
        });
      }, interval);

      return () => clearInterval(progressInterval);
    }
  }, [duration, onClose]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 400);
  };

  const getTypeConfig = () => {
    switch (type) {
      case 'success':
        return {
          icon: 'check_circle',
          bgClass: 'toast-success',
          iconColor: 'var(--success)'
        };
      case 'error':
        return {
          icon: 'error',
          bgClass: 'toast-error',
          iconColor: 'var(--error)'
        };
      case 'info':
        return {
          icon: 'info',
          bgClass: 'toast-info',
          iconColor: 'var(--info)'
        };
      case 'warning':
        return {
          icon: 'warning',
          bgClass: 'toast-warning',
          iconColor: 'var(--warning)'
        };
      default:
        return {
          icon: 'notifications',
          bgClass: 'toast-default',
          iconColor: 'var(--text-primary)'
        };
    }
  };

  const config = getTypeConfig();

  return (
    <div
      className={`toast-container ${config.bgClass} ${isClosing ? 'toast-closing' : ''}`}
      data-theme={effectiveTheme === 'dark' ? 'dark' : 'light'}
    >
      {/* Toast content that emerges from logo (left side) */}
      <div className="toast-content-wrapper">
        <div className="toast-content">
          <div className="toast-icon-wrapper" style={{ color: config.iconColor }}>
            <span className="material-symbols-outlined toast-icon">
              {config.icon}
            </span>
          </div>
          <div className="toast-message-wrapper">
            <p className="toast-message" role="status" aria-live={type === 'error' ? 'assertive' : 'polite'} aria-atomic="true">
              {message}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="toast-close-btn"
            aria-label="Close notification"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        {duration > 0 && (
          <div className="toast-progress-bar">
            <div
              className="toast-progress-fill"
              style={{
                width: `${progress}%`,
                backgroundColor: config.iconColor
              }}
            />
          </div>
        )}
      </div>

      {/* Logo anchor - fixed on the right */}
      <div className="toast-logo-anchor">
        <div className="toast-logo-wrapper">
          <img
            src={logoSrc}
            alt="Logo"
            className="toast-logo"
            loading="eager"
            decoding="async"
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageLoaded(true)}
            style={{
              opacity: imageLoaded ? 1 : 0.5,
              transition: 'opacity 0.2s ease-in'
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Toast;
